// src/pages/DynamicLandingPage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import NotFound from './NotFound';

const DEFAULT_IMAGE = 'https://studio.littlehibba.com/images/sajeda/6.jpg';

const DynamicLandingPage = () => {
    const { slug } = useParams();
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const fetchPage = async () => {
            try {
                const q = query(collection(db, 'customPages'), where('slug', '==', slug));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    setNotFound(true);
                } else {
                    setPageData(querySnapshot.docs[0].data());
                }
            } catch (error) {
                console.error("Error fetching landing page:", error);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        fetchPage();
    }, [slug]);

    if (loading) return <div className="min-vh-100 d-flex justify-content-center align-items-center"><Spinner animation="border" variant="primary" /></div>;
    if (notFound || !pageData) return <NotFound />;

    return (
        <div className="dynamic-landing-page-container pb-5" style={{ backgroundColor: '#fdfdfd' }}>
            <Helmet>
                <title>{pageData.title} | Little Hibba</title>
                {pageData.seoDescription && <meta name="description" content={pageData.seoDescription} />}
                {pageData.seoKeywords && <meta name="keywords" content={pageData.seoKeywords} />}
            </Helmet>
            
            <div className="container py-5" style={{ maxWidth: '1000px' }}>
                {pageData.contentModules?.map((block) => {
                    const s = block.style || {};
                    
                    return (
                        <div key={block.id} className="mb-4">
                            
                            {block.type === 'heading' && (
                                <h2 className={`text-${s.align} fw-bolder my-4`} style={{ color: s.color, fontSize: `${s.fontSize}px`, fontFamily: "'Playfair Display', serif" }}>{block.content}</h2>
                            )}

                            {block.type === 'text' && (
                                <p className={`text-${s.align} my-4`} style={{ color: s.color, fontSize: `${s.fontSize}px`, whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{block.content}</p>
                            )}

                            {block.type === 'image' && (
                                <div className={`text-${s.align} my-5`}>
                                    <img src={block.content || DEFAULT_IMAGE} alt="Canvas element" className="img-fluid shadow-sm" style={{ width: `${s.width}%`, borderRadius: `${s.borderRadius}px`, objectFit: 'cover' }} />
                                </div>
                            )}

                            {block.type === 'button' && (
                                <div className={`text-${s.align} my-5`}>
                                    <a href={block.url || '#'} className="btn shadow-sm fw-bold px-5 py-2" style={{ backgroundColor: s.bgColor, color: s.textColor, fontSize: `${s.fontSize}px`, borderRadius: `${s.borderRadius}px`, border: 'none', letterSpacing: '1px' }}>{block.content}</a>
                                </div>
                            )}

                            {block.type === 'html' && (
                                <div className="my-5" dangerouslySetInnerHTML={{ __html: block.content }} />
                            )}

                            {block.type === 'columns' && (
                                <div className="row g-4 my-5" style={{ backgroundColor: s.bgColor }}>
                                    {block.columns.map((col, i) => (
                                        <div key={i} className={`col-md-${12 / block.colCount} text-center`}>
                                            {col.image && <img src={col.image || DEFAULT_IMAGE} alt="Column Visual" className="img-fluid mb-3 rounded-4 shadow-sm" style={{ maxHeight: '250px', objectFit: 'cover' }} />}
                                            <h4 style={{ color: s.textColor, fontFamily: "'Playfair Display', serif" }}>{col.title}</h4>
                                            <p style={{ color: s.textColor, opacity: 0.8 }}>{col.text}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DynamicLandingPage;